// ConfirmReceiptModal — buyer confirms delivery (releases escrow).
// ReviewModal — buyer leaves a star rating + comment.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Btn } from "../../components/Btn";
import { Icon } from "../../components/Icon";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { itemTitle } from "./constants";

export function ConfirmReceiptModal({ sel, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    if (!sel?.id) return;
    setBusy(true);
    setError(null);
    const now = new Date().toISOString();

    const { error: subErr } = await supabase
      .from("sub_orders")
      .update({ status: "completed", received_at: now, completed_at: now })
      .eq("id", sel.id);
    if (subErr) {
      setBusy(false);
      setError(subErr.message || "Could not confirm. Please try again.");
      return;
    }

    // Best-effort escrow release. Mirrors the mobile flow: log on failure but
    // don't roll back the receipt — auto-release cron will catch it within 48h.
    const { error: escrowErr } = await supabase
      .from("escrow_ledger")
      .update({ status: "released", released_at: now })
      .eq("sub_order_id", sel.id);
    if (escrowErr) console.error("Escrow release error:", escrowErr.message);

    // Notify the seller. Lookup user_id from sellers row.
    try {
      if (sel.seller_id) {
        const { data: sd } = await supabase
          .from("sellers")
          .select("user_id")
          .eq("id", sel.seller_id)
          .maybeSingle();
        if (sd?.user_id) {
          const orderNum = sel.sub_number || sel.orders?.order_number || "order";
          const totalNgwee = sel.total || 0;
          const sellerPayoutNgwee = totalNgwee - Math.round(totalNgwee * 0.1);
          await supabase.from("notifications").insert({
            user_id: sd.user_id,
            type: "order",
            title: "Order Received & Payment Released! 💰",
            body: `Customer confirmed receipt of Order #${orderNum}. ${fmtK(sellerPayoutNgwee)} released to your account.`,
            data: { order_id: sel.id },
            channel: "in_app",
          });
        }
      }
    } catch (ne) {
      console.error("Seller notification failed:", ne);
    }

    setBusy(false);
    onDone?.();
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 400,
          background: C.white,
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, letterSpacing: "-0.3px" }}>
          Confirm Receipt
        </h2>
        <p style={{ fontSize: 13, color: C.slate, margin: "8px 0 0", lineHeight: 1.5 }}>
          Have you received this order? This will release payment to the seller and cannot be undone.
        </p>

        {error && (
          <div
            style={{
              marginTop: 14,
              padding: "8px 10px",
              background: C.redBg,
              color: C.red,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <Btn kind="outline" onClick={onClose} disabled={busy} full>
            Not Yet
          </Btn>
          <Btn onClick={submit} disabled={busy} full icon="check">
            {busy ? "Confirming…" : "Yes, Received"}
          </Btn>
        </div>
      </div>
    </div>
  );
}


export function ReviewModal({ sel, user, onClose }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  async function submit() {
    if (!user?.id || !sel?.id || !sel.seller_id) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.from("reviews").insert({
      sub_order_id: sel.id,
      buyer_id: user.id,
      seller_id: sel.seller_id,
      rating,
      text: text.trim() || null,
    });
    setBusy(false);
    if (err) {
      // sub_order_id is UNIQUE — friendly message on dup.
      setError(err.code === "23505" ? "You have already reviewed this order." : err.message);
      return;
    }
    setDone(true);
    setTimeout(onClose, 1200);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: C.white,
          borderRadius: 20,
          padding: 24,
          boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, letterSpacing: "-0.3px" }}>
          Rate your order
        </h2>
        <p style={{ fontSize: 12.5, color: C.muted, margin: "6px 0 18px" }}>
          How was your experience with {sel.sellers?.store_name || "this seller"}?
        </p>

        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                fontSize: 30,
                color: n <= rating ? C.amber : C.dustDk,
                lineHeight: 1,
              }}
            >
              ★
            </button>
          ))}
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 500))}
          placeholder="Share a quick note (optional)…"
          rows={4}
          style={{
            width: "100%",
            padding: 12,
            fontSize: 13,
            fontFamily: "inherit",
            background: C.cream,
            border: "1px solid rgba(196,193,183,0.4)",
            borderRadius: 12,
            color: C.charcoal,
            outline: "none",
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />

        {error && (
          <div style={{ marginTop: 12, padding: "8px 10px", background: C.redBg, color: C.red, borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
            {error}
          </div>
        )}
        {done && (
          <div style={{ marginTop: 12, padding: "8px 10px", background: C.greenBg, color: C.hunter, borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
            Thanks for your review!
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <Btn kind="outline" onClick={onClose} full>
            Cancel
          </Btn>
          <Btn onClick={submit} disabled={busy || done} full icon="star">
            {busy ? "Submitting…" : "Submit review"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
